type RuntimeEnvMode = 'always' | 'production-only';

interface RuntimeWindowLike {
  __ENV__?: Record<string, string>;
  location?: {
    hostname?: string;
  };
}

interface ResolveEnvValueOptions {
  windowEnvKey: string;
  viteGlobal: string | undefined;
  processEnvKey: string;
  devDefault: string;
  runtimeEnvMode?: RuntimeEnvMode;
  devFlag?: boolean;
  consoleTarget?: Pick<Console, 'error'>;
}

function isNonEmpty(value: string | undefined | null): value is string {
  return typeof value === 'string' && value.length > 0;
}

function getRuntimeWindow(): RuntimeWindowLike | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return window as RuntimeWindowLike;
}

export function isProductionRuntime(devFlag?: boolean): boolean {
  if (typeof devFlag === 'boolean') {
    return !devFlag;
  }

  const runtimeWindow = getRuntimeWindow();
  if (runtimeWindow?.location?.hostname) {
    const hostname = runtimeWindow.location.hostname;
    return hostname !== 'localhost' && hostname !== '127.0.0.1' && hostname !== '::1';
  }

  return false;
}

export function getOptionalEnvValue(
  windowEnvKey: string,
  viteGlobal: string | undefined,
  processEnvKey: string,
  devDefault = '',
): string {
  const runtimeWindow = getRuntimeWindow();
  const windowEnv = runtimeWindow?.__ENV__;
  if (windowEnv && isNonEmpty(windowEnv[windowEnvKey])) {
    return windowEnv[windowEnvKey];
  }

  if (isNonEmpty(viteGlobal)) {
    return viteGlobal;
  }

  if (typeof process !== 'undefined' && process.env && isNonEmpty(process.env[processEnvKey] as string)) {
    return process.env[processEnvKey] as string;
  }

  return devDefault;
}

export function getEnvValue({
  windowEnvKey,
  viteGlobal,
  processEnvKey,
  devDefault,
  runtimeEnvMode = 'always',
  devFlag,
  consoleTarget = console,
}: ResolveEnvValueOptions): string {
  const production = isProductionRuntime(devFlag);
  const runtimeWindow = getRuntimeWindow();
  const canUseRuntimeEnv = runtimeEnvMode === 'always' || production;

  if (canUseRuntimeEnv) {
    const windowEnv = runtimeWindow?.__ENV__;
    if (windowEnv && isNonEmpty(windowEnv[windowEnvKey])) {
      return windowEnv[windowEnvKey];
    }
  }

  if (isNonEmpty(viteGlobal)) {
    return viteGlobal;
  }

  if (typeof process !== 'undefined' && process.env && isNonEmpty(process.env[processEnvKey])) {
    return process.env[processEnvKey] as string;
  }

  if (production) {
    consoleTarget.error(`[ENV] Missing required config: ${windowEnvKey}. Check env-config.js or .env.production.`);
  }

  return devDefault;
}
